import { leaderboardContract } from '@daymade/api-contracts';
import { ClientInferResponseBody } from '@ts-rest/core';
import CardList from 'components/CardList';
import Chevron from 'components/Chevron';
import GlowBar from 'components/GlowBar';
import HeroImageBackgroundContainer from 'components/HeroImageBackgroundContainer';
import HeroSection from 'components/HeroSection';
import MenuSection from 'components/layout/header/MenuSection';
import MainLayout from 'components/layout/MainLayout';
import DashboardBar from 'components/leaderboard/DashboardBar';
import LeaderboardPositionWinnersTable from 'components/leaderboard/LeaderboardPositionWinnersTable';
import LeaderboardPrize from 'components/leaderboard/LeaderboardPrize';
import LeaderboardPrizeTable from 'components/leaderboard/LeaderboardPrizeTable';
import LeaderboardTable, {
  LeaderboardTableRow,
} from 'components/leaderboard/LeaderboardTable';
import PreviousLeaderboardRafflePrizeWin from 'components/leaderboard/PreviousLeaderboardRafflePrizeWin';
import RafflePrizeCard from 'components/subscribers/RafflePrizeCard';
import RafflePrizeModal from 'components/subscribers/RafflePrizeModal';
import ZigzagFeatureList, {
  Alignment,
  ZigzagFeature,
} from 'components/ZigzagFeatureList';
import { useUserStore } from 'hooks/stores/useUserStore';
import { observer } from 'mobx-react-lite';
import { NextSeo } from 'next-seo';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { leaderboardApiClient } from 'utils/apiClient';
import { scrollToSection, stringFromDate } from 'utils/common';
import { ASSET_PREFIX, colors } from 'utils/constants';
import HttpStatusCode from 'utils/HttpStatusCode';

export const leaderboardPrizeDesktopSplitWidth = '700px';

type TLeaderboardDraw = ClientInferResponseBody<
  typeof leaderboardContract.public.getCurrentLeaderboardDraw,
  200
>;

type TLeaderboardRanking = ClientInferResponseBody<
  typeof leaderboardContract.public.getLeaderboardRanking,
  200
>;

type TLeaderboardWinners = ClientInferResponseBody<
  typeof leaderboardContract.public.getLastMonthsWinners,
  200
>['data'];

type TLeaderboardPointsBreakdown = ClientInferResponseBody<
  typeof leaderboardContract.auth.getPointsBreakdown,
  200
>;

interface ILeaderboardProps {
  upcomingDraw: TLeaderboardDraw['draw'];
  leaderboardData: TLeaderboardRanking['leaderboard'];
  lastMonthsWinners: TLeaderboardWinners;
}

const Leaderboard = observer(
  ({ upcomingDraw, leaderboardData, lastMonthsWinners }: ILeaderboardProps) => {
    const router = useRouter();
    const { currentUser, loadLeaderboardStats } = useUserStore();

    const [showCurrentRafflePrizeModal, setShowCurrentRafflePrizeModal] =
      useState<boolean>();

    const lastMonthsPrize = {
      heading: lastMonthsWinners?.prize.title || '',
      backgroundImage: lastMonthsWinners?.prize.imageLocations.main,
      includes: lastMonthsWinners?.prize.whatsIncludedBody,
    };

    const upcomingPrize = {
      heading: upcomingDraw.prize.title,
      backgroundImage: upcomingDraw.prize.imageLocations.main,
      includes: upcomingDraw.prize.whatsIncludedBody,
    };

    const [pointsBreakdown, setPointsBreakdown] =
      useState<TLeaderboardPointsBreakdown['breakdown']>();

    const showResults = router.query.showResults === 'true';

    const preExpandResultsSection = showResults;

    // There's a bug with the card list component inside last month's results section where it doesn't
    // display correctly on mobile unless the section is expanded on page load.
    // This is a hacky fix: the section is expanded on page load and then instantly collapsed.
    const [lastMonthsResultsExpanded, setLastMonthsResultsExpanded] =
      useState(true);

    useEffect(() => {
      if (preExpandResultsSection) {
        scrollToSection('#resultsSection');
      } else {
        setLastMonthsResultsExpanded(false);
      }
    }, [preExpandResultsSection]);

    useEffect(() => {
      (async () => {
        if (currentUser) {
          await loadLeaderboardStats();

          const resp = await leaderboardApiClient.auth.getPointsBreakdown();
          if (resp.status === HttpStatusCode.OK) {
            setPointsBreakdown(resp.body.breakdown);
          } else {
            console.error(
              'Failed to fetch points breakdown:',
              resp.body.message,
            );
          }
        }
      })();
    }, [currentUser, loadLeaderboardStats]);

    return (
      <MainLayout>
        <NextSeo
          title="Leaderboard | Win Epic Prizes | DAYMADE"
          description="We've launched our Leaderboard, meaning you have even more chances to win with DAYMADE! Collect points simply by playing, reviewing us, referring a friend & more. The more points, the more chance of winning!"
          openGraph={{
            title: 'Leaderboard | Win Epic Prizes | DAYMADE',
            description:
              "We've launched our Leaderboard, meaning you have even more chances to win with DAYMADE! Collect points simply by playing, reviewing us, referring a friend & more. The more points, the more chance of winning!",
          }}
        />
        <HeroSection showUpcomingDrawDate={false} height="350px" leaderboard>
          <img
            src={`${ASSET_PREFIX}/images/leaderboard/leaderboard.png`}
            style={{ height: '100px' }}
            alt="FAQ icon"
          />
          <p
            style={{
              color: colors.brand.neon,
              textShadow: `0 0 8px ${colors.brand.shadow}`,
              margin: '10px 0 0px 0',
              fontStyle: 'italic',
              fontSize: '1.3em',
              fontWeight: 'bold',
            }}
          >
            Welcome to the
          </p>
          <h1
            style={{
              color: colors.brand.neon,
              textShadow: `0 0 8px ${colors.brand.shadow}`,
              marginTop: '0px',
            }}
          >
            <span className="blueOutlineText">Leaderboard</span>
          </h1>
        </HeroSection>
        <div
          className="flexCentered"
          style={{ width: '100%', paddingBottom: '2em' }}
        >
          {leaderboardData?.length > 0 && (
            <>
              <div
                onClick={() => scrollToSection('#leaderboardSection')}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  fontWeight: 600,
                  fontStyle: 'italic',
                  cursor: 'pointer',
                  fontSize: '1.1em',
                  padding: '0.3em 2em',
                }}
              >
                View Leaderboard
                <Chevron
                  direction="right"
                  style={{
                    color: colors.primaryBrandGray,
                    display: 'inline-flex',
                    marginLeft: '1em',
                    verticalAlign: 'middle',
                  }}
                />
              </div>

              <div
                className="separator"
                style={{ margin: '1em auto 1.2em auto', width: '80%' }}
              />
            </>
          )}

          <img
            src={`${ASSET_PREFIX}/images/leaderboard/crown-blue.svg`}
            style={{ width: '40px', height: '40px' }}
          />

          <h3 style={{ marginTop: '0.2em' }}>
            THE <strong>PRIZES</strong>
          </h3>

          {showCurrentRafflePrizeModal && (
            <RafflePrizeModal
              heading={upcomingPrize.heading}
              includes={upcomingPrize.includes}
              backgroundImage={upcomingPrize.backgroundImage}
              handleDismiss={() =>
                setShowCurrentRafflePrizeModal(!showCurrentRafflePrizeModal)
              }
            />
          )}

          <CardList
            id="prizesCardList"
            style={{ minWidth: '100%', marginTop: '1em' }}
            withScrollButtons={false}
          >
            <LeaderboardPrize
              descriptionComponent={
                <>
                  Everyone on the leaderboard gets entered into the Leaderboard
                  Raffle, with a guaranteed prize <strong>worth £150</strong>{' '}
                  every month!
                </>
              }
              cardContentComponent={
                <RafflePrizeCard
                  heading={upcomingPrize.heading}
                  backgroundImage={`url(${upcomingPrize.backgroundImage})`}
                  onClick={() =>
                    setShowCurrentRafflePrizeModal(!showCurrentRafflePrizeModal)
                  }
                  style={{
                    height: '200px',
                    margin: 0,
                  }}
                />
              }
            />

            <LeaderboardPrize
              descriptionComponent={
                <>
                  The 3 players who collect the most points & rank highest on
                  the Leaderboard on the second Tuesday of the month at 5pm get
                  awarded the following <strong>EPIC prizes</strong>:
                </>
              }
              cardContentComponent={
                <div className="leaderboardPrizeList">
                  <img
                    src={`${ASSET_PREFIX}/images/leaderboard/1st.png`}
                    style={{
                      width: '32px',
                      height: '32px',
                      gridColumn: '1 / 2',
                      gridRow: '1 / 2',
                    }}
                  />
                  <p
                    style={{
                      gridColumn: '2 / 3',
                      gridRow: '1 / 2',
                      alignItems: 'center',
                      fontWeight: 600,
                      fontSize: '1.2em',
                    }}
                  >
                    <Link
                      href="/prizes?tier=Bronze"
                      style={{
                        margin: 0,
                        fontWeight: 'bold',
                        whiteSpace: 'unset',
                      }}>
                      
                        Your choice of ANY Bronze prize
                      
                    </Link>
                  </p>

                  <img
                    src={`${ASSET_PREFIX}/images/leaderboard/2nd.png`}
                    style={{
                      width: '32px',
                      height: '32px',
                      gridColumn: '1 / 2',
                      gridRow: '2 / 3',
                    }}
                  />
                  <p
                    style={{
                      gridColumn: '2 / 3',
                      gridRow: '2 / 3',
                      alignItems: 'center',
                      fontWeight: 600,
                      fontSize: '1.2em',
                    }}
                  >
                    <Link
                      href="/prizes/details/gift-card-15"
                      style={{
                        margin: 0,
                        fontWeight: 'bold',
                        whiteSpace: 'unset',
                      }}>
                      
                        £15 gift card to spend on your fave brands!
                      
                    </Link>
                  </p>

                  <img
                    src={`${ASSET_PREFIX}/images/leaderboard/3rd.png`}
                    style={{
                      width: '32px',
                      height: '32px',
                      gridColumn: '1 / 2',
                      gridRow: '3 / 4',
                    }}
                  />
                  <p
                    style={{
                      gridColumn: '2 / 3',
                      gridRow: '3 / 4',
                      alignItems: 'center',
                      fontWeight: 600,
                      fontSize: '1.2em',
                    }}
                  >
                    <a
                      href="https://grind.co.uk/products/beanandground"
                      target="_blank"
                      style={{
                        fontWeight: 'bold',
                        margin: 0,
                        whiteSpace: 'normal',
                      }}
                    >
                      Grind Coffee Tin + House Blend Ground Coffee
                    </a>
                  </p>
                </div>
              }
            />
          </CardList>

          <div
            className="scrollToSeeMoreLabel"
            style={{
              fontWeight: 600,
              fontStyle: 'italic',
              fontSize: '1.1em',
              padding: '0.5em 2em 0 2em',
            }}
          >
            scroll to see other prizes
          </div>

          <div className="paddedMaxWidthContainer" style={{ width: '100%' }}>
            <div
              className="separator"
              style={{ margin: '1em 2em 1.2em 2em', width: 'calc(100% - 4em)' }}
            />

            <MenuSection
              titleComponent={
                <>
                  WHAT IS THE <strong>LEADERBOARD</strong>?
                </>
              }
              style={{
                width: 'auto',
                margin: '0 1em',
              }}
              children={
                <div
                  style={{
                    border: '3px solid #DDD',
                    fontWeight: 600,
                    padding: '2em 2em 3em 2em',
                    borderRadius: '4px',
                    margin: '0 2em',
                  }}
                >
                  <span className="bigFirstLetter" style={{ fontSize: '22px' }}>
                    T
                  </span>
                  he DAYMADE Leaderboard is our way to reward our DAYMADE
                  community!
                  <br />
                  <br />
                  Each time you take an action on our site (like enter a draw,
                  refer a friend, etc.), you collect points.
                  <br />
                  <br />
                  You can see how many points you collect per action in{' '}
                  <strong>the table below</strong>. This way, we are giving you
                  even more chances to win!
                </div>
              }
            />

            <div
              className="separator"
              style={{ margin: '1em 2em 1.2em 2em', width: 'calc(100% - 4em)' }}
            />

            <MenuSection
              titleComponent={
                <>
                  GOOD TO <strong>KNOW</strong>
                </>
              }
              style={{
                width: 'auto',
                margin: '0 1em',
              }}
              children={
                <div
                  style={{
                    border: '3px solid #DDD',
                    fontWeight: 600,
                    borderRadius: '4px',
                    margin: '0 2em',
                  }}
                >
                  <ZigzagFeatureList>
                    <ZigzagFeature
                      alignment={Alignment.LEFT}
                      imageComponent={
                        <img
                          src={`${ASSET_PREFIX}/images/leaderboard/draw-date-calendar.svg`}
                          style={{ width: '30px', marginLeft: '12px' }}
                        />
                      }
                    >
                      <h4>
                        DRAW <strong>DAY</strong>
                      </h4>

                      <p>
                        The winners of the Leaderboard Raffle and the position
                        prizes are all drawn/decided on the{' '}
                        <strong>second Tuesday of every month at 5pm</strong>.
                      </p>
                    </ZigzagFeature>

                    <ZigzagFeature
                      alignment={Alignment.RIGHT}
                      imageComponent={
                        <img
                          src={`${ASSET_PREFIX}/images/leaderboard/email.svg`}
                          style={{ width: '35px', marginRight: '12px' }}
                        />
                      }
                    >
                      <h4>
                        <strong>EMAIL</strong>
                      </h4>

                      <p>
                        All winners will be emailed as soon as the draw has
                        taken takes place.
                      </p>
                    </ZigzagFeature>

                    <ZigzagFeature
                      alignment={Alignment.LEFT}
                      imageComponent={
                        <img
                          src={`${ASSET_PREFIX}/images/leaderboard/board-reset.svg`}
                          style={{ width: '30px', marginLeft: '12px' }}
                        />
                      }
                    >
                      <h4>
                        POINTS <strong>RESET</strong>
                      </h4>

                      <p>
                        Leaderboard points automatically reset to 0 at{' '}
                        <strong>
                          5pm on the second Tuesday of every month
                        </strong>{' '}
                        after the winners are drawn.
                      </p>
                    </ZigzagFeature>
                  </ZigzagFeatureList>
                </div>
              }
            />

            {lastMonthsWinners && (
              <>
                <div
                  className="separator"
                  style={{
                    margin: '1em 2em 1.2em 2em',
                    width: 'calc(100% - 4em)',
                  }}
                />

                <MenuSection
                  id="resultsSection"
                  expanded={lastMonthsResultsExpanded}
                  setExpanded={setLastMonthsResultsExpanded}
                  titleComponent={
                    <>
                      LAST MONTH'S <strong>RESULTS</strong>
                    </>
                  }
                  style={{
                    width: 'auto',
                    margin: '0 1em',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      margin: '1em 2em 0 2em',
                      fontStyle: 'italic',
                      width: 'auto',
                    }}
                  >
                    <p style={{ fontWeight: 600 }}>Draw date:</p>

                    <p>
                      <strong>
                        {lastMonthsWinners.drawDate
                          ? stringFromDate(new Date(lastMonthsWinners.drawDate))
                          : (console.warn(
                              'lastMonthsWinners.drawDate was nullish',
                            ) as undefined) && undefined}
                      </strong>
                      <br />
                    </p>
                  </div>

                  <div
                    className="separator"
                    style={{ margin: '0.2em 2em 1.2em 2em', width: 'auto' }}
                  />

                  <CardList
                    id="lastMonthsResultsCardList"
                    style={{ minWidth: '100%' }}
                    withScrollButtons={false}
                  >
                    <PreviousLeaderboardRafflePrizeWin
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        width: '50%',
                      }}
                    >
                      <h5 style={{ margin: '0 0 2em 0' }}>
                        POSITION <strong>WINNERS</strong>
                      </h5>

                      <LeaderboardPositionWinnersTable
                        winners={lastMonthsWinners.positionWinners}
                      />
                    </PreviousLeaderboardRafflePrizeWin>

                    <PreviousLeaderboardRafflePrizeWin>
                      <h5 style={{ margin: '0 0 2em 0' }}>
                        RAFFLE <strong>WINNER</strong>
                      </h5>

                      <RafflePrizeCard
                        heading={lastMonthsPrize.heading}
                        backgroundImage={`url(${lastMonthsPrize.backgroundImage})`}
                        style={{
                          height: '200px',
                          margin: 0,
                        }}
                      />

                      <p style={{ margin: '2em', textAlign: 'center' }}>
                        <strong>won by:</strong>
                      </p>

                      <LeaderboardTableRow
                        rank={1}
                        leaderboardRowGroup={{
                          position: 1,
                          positionLeaderboardRows:
                            lastMonthsWinners.raffleWinner
                              ? [lastMonthsWinners.raffleWinner]
                              : [],
                        }}
                        style={{
                          boxShadow: 'none',
                          border: '2px solid #DDD',
                          marginBottom: 0,
                        }}
                      />
                    </PreviousLeaderboardRafflePrizeWin>
                  </CardList>

                  <div
                    className="scrollToSeeMoreLabel"
                    style={{
                      fontWeight: 600,
                      fontStyle: 'italic',
                      fontSize: '1.1em',
                      padding: '0.5em 2em 0 2em',
                    }}
                  >
                    scroll to see other winners
                  </div>
                </MenuSection>
              </>
            )}
          </div>
        </div>
        {leaderboardData?.length > 0 && (
          <div className="sectionContainer" id="leaderboardSection">
            <GlowBar color="#DDD" />

            <h3
              style={{
                textShadow: `2px 2px ${colors.brand.pastel}`,
                marginTop: '2em',
              }}
            >
              THE <strong>LEADERBOARD</strong>
            </h3>

            <p
              style={{ fontStyle: 'italic', fontWeight: 'bold', color: '#666' }}
            >
              This Month's Top 100 Players
            </p>

            <p
              style={{ fontStyle: 'italic', fontWeight: 'bold', color: '#666' }}
            >
              (Points reset monthly)
            </p>

            <LeaderboardTable leaderboardData={leaderboardData} />

            <GlowBar
              direction="up"
              color="#DDD"
              className="glowBar"
              marginTop="2em"
            />
          </div>
        )}
        <HeroImageBackgroundContainer nonMoving>
          <p
            style={{
              color: colors.brand.neon,
              textShadow: `0 0 8px ${colors.brand.shadow}`,
              margin: '0 0 5px 0',
              fontStyle: 'italic',
              fontSize: '1.1em',
              fontWeight: 'bold',
            }}
          >
            How to
          </p>
          <h3
            style={{
              color: colors.brand.neon,
              textShadow: `0 0 8px ${colors.brand.shadow}`,
              marginTop: '0px',
            }}
          >
            COLLECT <span className="blueOutlineText">POINTS</span>
          </h3>

          <p style={{ color: 'white', fontWeight: 'bold' }}>
            25 points = 1 entry into Leaderboard Raffle
          </p>

          <LeaderboardPrizeTable />

          <p
            style={{
              color: 'white',
              fontWeight: 'bold',
              marginTop: '2em',
              fontStyle: 'italic',
            }}
          >
            Points reset every month
          </p>

          <div style={{ height: '3em' }} />
        </HeroImageBackgroundContainer>
        {pointsBreakdown && <DashboardBar pointsBreakdown={pointsBreakdown} />}
        <style jsx>{`
          :global(.prizeContainer:last-child) {
            padding-right: 2em;
            margin-left: 1.5em;
          }

          :global(.prizeContainer:not(:last-child)) {
            margin-right: 1.5em;
          }

          .scrollToSeeMoreLabel {
            text-align: center;
          }

          .leaderboardPrizeList {
            background: white;
            border-radius: 4px;
            border: 2px solid #ccc;
            height: 200px;
            display: grid;
            grid-template-columns: 30px calc(92.5% - 30px);
            gap: 7.5%;
            grid-template-rows: repeat(3, 1fr);
            padding: 2em;
            align-items: center;
            justify-content: center;
          }

          .leaderboardPrizeList p {
            margin-bottom: 0;
          }

          @media (min-width: ${leaderboardPrizeDesktopSplitWidth}) {
            .scrollToSeeMoreLabel {
              display: none;
            }

            :global(.cardList) {
              justify-content: space-around;
              min-width: auto !important;
              max-width: 1100px !important;
            }

            :global(.prizeContainer:last-child) {
              padding-right: 0;
            }
          }
        `}</style>
      </MainLayout>
    );
  },
);

export const getServerSideProps = async () => {
  const drawResult =
    await leaderboardApiClient.public.getCurrentLeaderboardDraw();

  if (drawResult.status !== HttpStatusCode.OK) {
    return {
      notFound: true,
    };
  }

  const upcomingDraw = drawResult.body.draw;

  console.log('Upcoming draw:', upcomingDraw);
  console.log('upcomingDraw.draw.id:', upcomingDraw.draw?.id);
  const leaderboardDataResult =
    await leaderboardApiClient.public.getLeaderboardRanking({
      query: {
        limit: 100,
      },
    });

  console.log('Leaderboard data result:', leaderboardDataResult);

  if (leaderboardDataResult.status !== HttpStatusCode.OK) {
    return {
      notFound: true,
    };
  }

  const leaderboardData = leaderboardDataResult.body.leaderboard;

  if (upcomingDraw.draw) {
    upcomingDraw.draw.drawDate =
      upcomingDraw.draw.drawDate?.toISOString() as any;
  }

  const lastMonthsWinnersResult =
    await leaderboardApiClient.public.getLastMonthsWinners();

  console.log("Last month's winners result:", lastMonthsWinnersResult);

  if (lastMonthsWinnersResult.status !== HttpStatusCode.OK) {
    return {
      notFound: true,
    };
  }
  const lastMonthsWinners = lastMonthsWinnersResult.body.data;

  if (lastMonthsWinners && lastMonthsWinners.drawDate) {
    lastMonthsWinners.drawDate =
      lastMonthsWinners.drawDate.toISOString() as any;
  }

  return {
    props: {
      upcomingDraw,
      leaderboardData,
      lastMonthsWinners,
    },
  };
};

export default Leaderboard;
